// Motor de cálculo de nómina colombiana. Funciones puras (sin UI, sin I/O):
// reciben datos y parámetros, devuelven un desglose completo de cada concepto.
// Convención de días: mes comercial de 30 días (año de 360), la misma que usa
// la liquidación de nómina y prestaciones sociales en Colombia.

function round(n) {
  return Math.round(Number(n) || 0);
}

// Cuenta los días entre dos fechas (ambas inclusive) usando la convención
// comercial de meses de 30 días: un mes completo (ej. 01 a 30, o 01 a 31)
// siempre cuenta 30 días, y cada quincena de cualquier mes cuenta 15 días.
export function diasComercialesEntre(fechaDesdeStr, fechaHastaStr) {
  const [y1, m1, d1raw] = fechaDesdeStr.split("-").map(Number);
  const [y2, m2, d2raw] = fechaHastaStr.split("-").map(Number);
  const d1 = Math.min(d1raw, 30);
  const d2 = Math.min(d2raw, 30);
  return (y2 - y1) * 360 + (m2 - m1) * 30 + (d2 - d1) + 1;
}

// Intersecta el período de liquidación con la fecha de ingreso del empleado
// (y, si se provee, su fecha de retiro) para no pagar días anteriores al
// ingreso ni posteriores al retiro.
function periodoEfectivo(periodo, empleado) {
  let desde = periodo.desde;
  let hasta = periodo.hasta;
  if (empleado.fecha_ingreso && empleado.fecha_ingreso > desde) desde = empleado.fecha_ingreso;
  if (empleado.fecha_retiro && empleado.fecha_retiro < hasta) hasta = empleado.fecha_retiro;
  return { desde, hasta };
}

export function calcularDevengado(empleado, periodo, parametrosNomina, extras = {}) {
  const { desde, hasta } = periodoEfectivo(periodo, empleado);
  const diasPeriodo = desde <= hasta ? diasComercialesEntre(desde, hasta) : 0;

  const salarioBase = Number(empleado.salario_base) || 0;
  const salarioProporcional = round((salarioBase / 30) * diasPeriodo);

  const topeAuxilio = parametrosNomina.smlmv * parametrosNomina.auxilio_transporte_tope_smlmv;
  const aplicaAuxilioTransporte = salarioBase > 0 && salarioBase <= topeAuxilio;
  const auxilioTransporte = aplicaAuxilioTransporte
    ? round((parametrosNomina.auxilio_transporte / 30) * diasPeriodo)
    : 0;

  const horasExtra = extras.horasExtra || []; // [{ concepto, horas, valor }]
  const totalHorasExtra = horasExtra.reduce((s, h) => s + (Number(h.valor) || 0), 0);

  const otrosDevengados = extras.otrosDevengados || []; // [{ concepto, valor, constituyeSalario? }]
  const totalOtros = otrosDevengados.reduce((s, o) => s + (Number(o.valor) || 0), 0);
  const totalOtrosSalariales = otrosDevengados
    .filter(o => o.constituyeSalario !== false)
    .reduce((s, o) => s + (Number(o.valor) || 0), 0);

  const comisiones = Number(extras.comisiones) || 0;

  const totalDevengado = salarioProporcional + auxilioTransporte + totalHorasExtra + totalOtros + comisiones;

  // IBC (ingreso base de cotización para seguridad social): excluye el
  // auxilio de transporte, que por ley no constituye salario para este fin.
  const ibcBruto = salarioProporcional + totalHorasExtra + totalOtrosSalariales + comisiones;
  const topeIBC = ((parametrosNomina.smlmv * 25) / 30) * diasPeriodo;
  const ibc = Math.min(ibcBruto, topeIBC);

  return {
    periodoDesde: desde, periodoHasta: hasta, diasPeriodo,
    salarioBase, salarioProporcional,
    aplicaAuxilioTransporte, auxilioTransporte,
    horasExtra, totalHorasExtra,
    otrosDevengados, totalOtros, totalOtrosSalariales,
    comisiones,
    totalDevengado, ibc
  };
}

export function calcularDeducciones(devengado, parametrosNomina) {
  const { ibc, diasPeriodo } = devengado;

  const saludEmpleado = round(ibc * (parametrosNomina.salud_empleado_pct / 100));
  const pensionEmpleado = round(ibc * (parametrosNomina.pension_empleado_pct / 100));

  // El Fondo de Solidaridad Pensional se define en tramos según cuántos SMLMV
  // representa el salario mensual equivalente; el % del tramo aplica sobre
  // todo el IBC del período (no solo sobre el excedente).
  const ibcMensualEquivalente = diasPeriodo > 0 ? (ibc / diasPeriodo) * 30 : 0;
  const multiploSMLMV = parametrosNomina.smlmv > 0 ? ibcMensualEquivalente / parametrosNomina.smlmv : 0;
  const tramo = (parametrosNomina.fondo_solidaridad_tramos || []).find(t =>
    multiploSMLMV >= t.desde_smlmv && (t.hasta_smlmv == null || multiploSMLMV < t.hasta_smlmv)
  );
  const fondoSolidaridadPct = tramo ? tramo.pct : 0;
  const fondoSolidaridad = round(ibc * (fondoSolidaridadPct / 100));

  const totalDeducciones = saludEmpleado + pensionEmpleado + fondoSolidaridad;

  return { ibc, saludEmpleado, pensionEmpleado, multiploSMLMV, fondoSolidaridadPct, fondoSolidaridad, totalDeducciones };
}

export function calcularAportesPatronales(devengado, empleado, parametrosNomina) {
  const { ibc } = devengado;

  const saludPatronal = parametrosNomina.exonera_salud_patronal
    ? 0
    : round(ibc * (parametrosNomina.salud_patronal_pct / 100));
  const pensionPatronal = round(ibc * (parametrosNomina.pension_patronal_pct / 100));

  const nivelRiesgo = empleado.nivel_riesgo_arl || "I";
  const arlPct = (parametrosNomina.tabla_arl || {})[nivelRiesgo] ?? 0;
  const arl = round(ibc * (arlPct / 100));

  const sena = parametrosNomina.exonera_sena_icbf ? 0 : round(ibc * (parametrosNomina.sena_pct / 100));
  const icbf = parametrosNomina.exonera_sena_icbf ? 0 : round(ibc * (parametrosNomina.icbf_pct / 100));
  const cajaCompensacion = round(ibc * (parametrosNomina.caja_compensacion_pct / 100));

  const totalAportesPatronales = saludPatronal + pensionPatronal + arl + sena + icbf + cajaCompensacion;

  return { saludPatronal, pensionPatronal, nivelRiesgo, arlPct, arl, sena, icbf, cajaCompensacion, totalAportesPatronales };
}

export function calcularProvisionPrestaciones(devengado, parametrosNomina) {
  // Cesantías, intereses de cesantías y prima se liquidan sobre todo lo que
  // constituye salario, incluido el auxilio de transporte. Las vacaciones se
  // liquidan solo sobre el salario ordinario (excluye auxilio de transporte
  // y horas extra ocasionales).
  const baseConAuxilio = devengado.salarioProporcional + devengado.totalHorasExtra +
    devengado.totalOtrosSalariales + devengado.comisiones + devengado.auxilioTransporte;
  const baseVacaciones = devengado.salarioProporcional + devengado.comisiones;

  const cesantias = round(baseConAuxilio * (parametrosNomina.cesantias_pct / 100));
  const interesesCesantias = round(baseConAuxilio * (parametrosNomina.intereses_cesantias_pct / 100));
  const prima = round(baseConAuxilio * (parametrosNomina.prima_pct / 100));
  const vacaciones = round(baseVacaciones * (parametrosNomina.vacaciones_pct / 100));

  const totalProvisionPrestaciones = cesantias + interesesCesantias + prima + vacaciones;

  return { baseConAuxilio, baseVacaciones, cesantias, interesesCesantias, prima, vacaciones, totalProvisionPrestaciones };
}

// Liquida un empleado completo para un período: junta las cuatro funciones
// anteriores y calcula el neto a pagar.
export function liquidarEmpleado(empleado, periodo, parametrosNomina, extras = {}) {
  const devengado = calcularDevengado(empleado, periodo, parametrosNomina, extras);
  const deducciones = calcularDeducciones(devengado, parametrosNomina);
  const aportesPatronales = calcularAportesPatronales(devengado, empleado, parametrosNomina);
  const provisionPrestaciones = calcularProvisionPrestaciones(devengado, parametrosNomina);
  const netoPagar = devengado.totalDevengado - deducciones.totalDeducciones - (Number(extras.otrasDeducciones) || 0);

  return { empleadoId: empleado.id, devengado, deducciones, aportesPatronales, provisionPrestaciones, netoPagar };
}
